import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Form, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router";
import { ThumbsDown, ThumbsUp } from "react-feather";
import { apiErrorMessage, useGet, useGetLazy } from "../../api";
import { EmptyState, FilterBar, FilterField, PageHeader, PageToolbar, ToolbarButton } from "../../components/ui";
import { ISmartShoppingSessionsResponse } from "../../interfaces";

const PAGE_SIZE = 50;

interface IFilter {
  search: string;
  accessLabel: string;
  rating: "" | "up" | "down";
  judge: "" | "fail";
}

const emptyFilter: IFilter = { search: "", accessLabel: "", rating: "", judge: "" };

export const formatCost = (usd: number | null | undefined) =>
  usd === null || usd === undefined ? "—" : usd < 0.01 ? `$${usd.toFixed(4)}` : `$${usd.toFixed(3)}`;

export const SmartShoppingSessions: React.FC = () => {
  const [draft, setDraft] = useState(emptyFilter);
  const [applied, setApplied] = useState(emptyFilter);
  const [page, setPage] = useState(0);
  const { data, loading, error, execute } = useGetLazy<ISmartShoppingSessionsResponse>();
  const { data: labels } = useGet<string[]>("smart-shopping/sessions/labels");

  // `execute` changes identity on auth renders; the ref keeps the effect from refiring.
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const load = useCallback((filter: IFilter, pageIndex: number) => {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => v && params.set(k, v));
    params.set("limit", String(PAGE_SIZE));
    params.set("skip", String(pageIndex * PAGE_SIZE));
    executeRef.current(`smart-shopping/sessions?${params}`).catch(() => undefined);
  }, []);

  useEffect(() => load(applied, page), [applied, page, load]);

  const activeCount = Object.values(applied).filter(Boolean).length;
  const total = data?.total ?? 0;

  return (
    <div>
      <PageHeader
        title="Smart Shopping sessions"
        description="Conversations from the Kindred Smart Shopping extension: what shoppers asked, what the agent did, what the judge thought and how people rated it."
      />
      <PageToolbar>
        <ToolbarButton label="Invite codes" icon="bi-key" to="/smart-shopping/access-codes" hint="Create, limit or revoke the codes people use to try the extension" />
        <ToolbarButton label="Refresh" icon="bi-arrow-clockwise" onClick={() => load(applied, page)} hint="Reload this list with the current filters" />
      </PageToolbar>

      <FilterBar
        onSearch={() => {
          setPage(0);
          setApplied(draft);
        }}
        onClear={() => {
          setDraft(emptyFilter);
          setApplied(emptyFilter);
          setPage(0);
        }}
        activeCount={activeCount}
        summary={data ? `${total.toLocaleString()} session${total === 1 ? "" : "s"}` : undefined}
      >
        <FilterField label="Search" hint="First message, a domain the agent visited, or a conversation id">
          <Form.Control size="sm" value={draft.search} onChange={(e) => setDraft({ ...draft, search: e.target.value })} placeholder="e.g. stroller or nike.com" />
        </FilterField>
        <FilterField label="Invite" hint="Which invite code the session used" md={3}>
          <Form.Select size="sm" value={draft.accessLabel} onChange={(e) => setDraft({ ...draft, accessLabel: e.target.value })}>
            <option value="">All invites</option>
            {(labels ?? []).map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </Form.Select>
        </FilterField>
        <FilterField label="Feedback" hint="Sessions with at least one rating of this kind" md={2}>
          <Form.Select size="sm" value={draft.rating} onChange={(e) => setDraft({ ...draft, rating: e.target.value as IFilter["rating"] })}>
            <option value="">Any</option>
            <option value="up">Thumbs up</option>
            <option value="down">Thumbs down</option>
          </Form.Select>
        </FilterField>
        <FilterField label="Judge" hint="Sessions where the judge failed an answer" md={3}>
          <Form.Check
            type="switch"
            id="judge-fail"
            label="Only judge failures"
            checked={draft.judge === "fail"}
            onChange={(e) => setDraft({ ...draft, judge: e.target.checked ? "fail" : "" })}
          />
        </FilterField>
      </FilterBar>

      {error && <Alert variant="danger">{apiErrorMessage(error)}</Alert>}
      {loading && !data && (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {data && data.items.length === 0 && (
        <EmptyState
          title="No sessions yet"
          message={activeCount ? "Nothing matches these filters." : "Sessions appear here once someone uses the extension with an invite code."}
          icon="bi-chat-square-text"
        />
      )}
      {data && data.items.length > 0 && (
        <>
          <Table hover responsive size="sm" className="align-middle">
            <thead>
              <tr>
                <th>Last turn</th>
                <th>First message</th>
                <th>Invite</th>
                <th className="text-end">Turns</th>
                <th>Stores</th>
                <th className="text-center">Feedback</th>
                <th
                  className="text-nowrap"
                  title="Turns that used Entity Mapping (EM), Promo Discovery (PD) and Merchant Map (MM), and how many used all three"
                >
                  Kindred APIs
                </th>
                <th className="text-end">Judge fails</th>
                <th className="text-end">Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.conversationId}>
                  <td className="text-nowrap small">{new Date(s.lastTurnAt).toLocaleString()}</td>
                  <td style={{ maxWidth: 380 }}>
                    <Link to={`/smart-shopping/sessions/${s.conversationId}`} className="text-truncate d-block">
                      {s.firstMessage || "(empty)"}
                    </Link>
                  </td>
                  <td className="small">{s.accessLabel ?? <span className="text-muted">Anonymous</span>}</td>
                  <td className="text-end">
                    {s.turnCount}
                    {s.failedTurns > 0 && (
                      <Badge bg="danger" className="ms-1" title="Turns that failed">
                        {s.failedTurns}
                      </Badge>
                    )}
                  </td>
                  <td className="small text-truncate" style={{ maxWidth: 200 }}>
                    {s.domains.slice(0, 3).join(", ")}
                    {s.domains.length > 3 && ` +${s.domains.length - 3}`}
                  </td>
                  <td className="text-center text-nowrap small">
                    {s.thumbsUp > 0 && (
                      <span className="text-success me-2">
                        <ThumbsUp size={13} /> {s.thumbsUp}
                      </span>
                    )}
                    {s.thumbsDown > 0 && (
                      <span className="text-danger">
                        <ThumbsDown size={13} /> {s.thumbsDown}
                      </span>
                    )}
                    {!s.thumbsUp && !s.thumbsDown && <span className="text-muted">—</span>}
                  </td>
                  <td className="text-nowrap small">
                    <span title="Turns using Entity Mapping">EM {s.apiTurns.productMap}</span>
                    <span className="mx-1 text-muted">·</span>
                    <span title="Turns using Promo Discovery">PD {s.apiTurns.promoDiscovery}</span>
                    <span className="mx-1 text-muted">·</span>
                    <span title="Turns using Merchant Map">MM {s.apiTurns.merchantMap}</span>
                    {s.apiTurns.allThree > 0 && (
                      <Badge bg="success" className="ms-2" title="Turns that used all three APIs">
                        {s.apiTurns.allThree}/{s.turnCount} all three
                      </Badge>
                    )}
                  </td>
                  <td className="text-end">{s.judgeFails > 0 ? <Badge bg="warning" text="dark">{s.judgeFails}</Badge> : <span className="text-muted">0</span>}</td>
                  <td className="text-end small">{formatCost(s.totalCostUsd)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between align-items-center small text-muted">
            <span>
              Showing {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} of {total.toLocaleString()}
            </span>
            <div className="d-flex gap-2">
              <Button size="sm" variant="outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button size="sm" variant="outline-secondary" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
